/**
 * /components/:widgetId/pricing for a SOLAR estimate (docs/12-solar-roof-scan.md
 * §9.3): the installer's system prices (base price + price per panel), the panel
 * they fit, their inverter steps and an optional battery, edited with
 * SolarPriceEditor and saved with the header's Save button
 * (api.updateWidget(id, { solarPricing })). Below it, "How the price is worked
 * out" shows what homeowners see for a few system sizes (SolarPriceExample).
 *
 * PricingTab (below) is what the router shows on the Pricing tab: this page for
 * solar estimates, the heat pump PricingPage (kW sizes, BUS grant, Instant
 * quotes) for everything else, so heat pump-only screens never show for solar.
 *
 * A widget that has never saved prices has solarPricing null: estimates then
 * use DEFAULT_SOLAR_PRICING (example prices). Those only work on the test page;
 * to go live the installer saves their own (the status check,
 * functions/widgets/validation.py pricing_missing), so Save stays available.
 */
import { useEffect, useMemo, useState } from "react";
import { DEFAULT_SOLAR_PRICING } from "@widget";
import { useApi } from "../../api";
import {
  SOLAR_PRICE_EXPLAINER,
  SolarPriceEditor,
  SolarPriceExample,
  fromSolarForm,
  hasSolarPrices,
  solarProblems,
  toSolarForm,
  type SolarForm,
} from "../../components/SolarPriceEditor";
import { PricingPage } from "./PricingPage";
import { useSaveBar, useTabDraft, useWidget } from "./WidgetLayout";
import "../../pricing.css";

/** The Pricing tab: system prices for solar estimates, kW sizes for heat pump estimators. */
export function PricingTab() {
  const { widget } = useWidget();
  return widget.product === "solar" ? <SolarPricingPage /> : <PricingPage />;
}

export function SolarPricingPage() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  // Never saved: start from the example prices, as the estimate does
  const neverSaved = !widget.solarPricing;
  const savedForm = useMemo(() => toSolarForm(widget.solarPricing ?? DEFAULT_SOLAR_PRICING), [widget.solarPricing]);
  // Kept by the layout so unsaved edits survive switching tabs ("pricing" marks the tab)
  const [form, setForm, discardDraft] = useTabDraft<SolarForm>("pricing", savedForm);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const edited = JSON.stringify(form) !== JSON.stringify(savedForm);
  // The example prices can be saved as they are, so Save is live until something is saved
  const dirty = edited || neverSaved;
  const problems = solarProblems(form);

  // ─── Leaving, saving and discarding ────────────────────────────────────────

  // Warn before leaving with unsaved edits (not for the untouched example prices)
  useEffect(() => {
    if (!edited) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [edited]);

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { solarPricing: fromSolarForm(form) }));
      discardDraft();
      setMessage({ tone: "success", text: "Prices saved. New estimates use them straight away." });
    } catch (e) {
      // The API's message is safe to show (it mirrors the checks in SolarPriceEditor)
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    discardDraft();
    setMessage(null);
  }

  // Save / Discard live in the widget header
  useSaveBar({ dirty, saving, blocked: problems.count > 0, onSave: save, onDiscard: discard });

  // ─── Page ──────────────────────────────────────────────────────────────────

  return (
    <div className="pricing">
      <section className="card">
        <h2>Your solar prices</h2>
        <p className="card-sub">
          A base price for the installation, a price per panel and the inverter you fit for each system size, all{" "}
          <strong>including installation and VAT</strong>. Homeowners choose how many panels they want on their roof and see the price for
          that system.
        </p>

        {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}
        {problems.count > 0 && (
          <div className="flash flash-danger">
            {problems.count === 1 ? "1 field needs attention" : `${problems.count} fields need attention`} before you can save.
          </div>
        )}
        {neverSaved && (
          <div className="flash flash-info">
            These are example prices. They work on the test page; check them, change them to yours and save before the estimate can go live
            on your website.
          </div>
        )}
        {!hasSolarPrices(form) && (
          <div className="flash flash-info">
            Set a base price, a price per panel and at least one inverter price: until then there's nothing to offer homeowners.
          </div>
        )}

        <SolarPriceEditor
          form={form}
          onChange={(next) => {
            setForm(next);
            setMessage(null);
          }}
        />
      </section>

      {/* The worked example: what homeowners see for a few system sizes */}
      <section className="card pricing-explain">
        <h2>How the price is worked out</h2>
        <p className="card-sub">{SOLAR_PRICE_EXPLAINER}</p>
        <SolarPriceExample form={form} />
      </section>
    </div>
  );
}
